import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface DirectMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  sender_name: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export const useDirectMessages = (otherUserId: string) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const myId = user?.id;

  const fetchMessages = useCallback(async () => {
    if (!myId || !otherUserId) return;
    
    const { data, error } = await supabase
      .from('direct_messages')
      .select('*')
      .or(`and(sender_id.eq.${myId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${myId})`)
      .order('created_at', { ascending: true })
      .limit(50);

    if (!error && data) {
      setMessages(data as DirectMessage[]);
    }
    setLoading(false);
  }, [myId, otherUserId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Realtime subscription for this specific conversation
  useEffect(() => {
    if (!myId || !otherUserId) return;

    const channel = supabase
      .channel(`dm-${myId}-${otherUserId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
      }, (payload) => {
        const msg = payload.new as DirectMessage;
        // Only add if it's part of this conversation
        const isRelevant = 
          (msg.sender_id === myId && msg.receiver_id === otherUserId) ||
          (msg.sender_id === otherUserId && msg.receiver_id === myId);
        
        if (isRelevant) {
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [myId, otherUserId]);

  // Mark messages as read
  useEffect(() => {
    if (!myId || !otherUserId) return;
    
    supabase
      .from('direct_messages')
      .update({ is_read: true })
      .eq('receiver_id', myId)
      .eq('sender_id', otherUserId)
      .eq('is_read', false)
      .then(() => {});
  }, [myId, otherUserId, messages]);

  const sendMessage = useCallback(async (text: string) => {
    if (!myId || !user?.name || !text.trim()) return;
    
    const { error } = await supabase
      .from('direct_messages')
      .insert({
        sender_id: myId,
        receiver_id: otherUserId,
        sender_name: user.name,
        message: text.trim(),
      });

    if (error) {
      console.error('Failed to send DM:', error);
    }
  }, [myId, otherUserId, user?.name]);

  return { messages, loading, sendMessage };
};
